import React, { useEffect, useState } from "react";
import FeaturedOn from "@/components/FeaturedOn";
import { useParams, useNavigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { api, track } from "@/lib/api";
import { useCart } from "@/context/CartContext";
import { Seo } from "@/components/Seo";
import { ogProduct, productJsonLd, breadcrumbJsonLd } from "@/lib/seo";
import { ShoppingBag, MapPin, ExternalLink, ChevronLeft, Check } from "lucide-react";

export default function ProductDetail() {
  const { id } = useParams();
  const [p, setP] = useState(null);
  const [size, setSize] = useState("M");
  const [qty, setQty] = useState(1);
  const [view, setView] = useState("front");
  const { add } = useCart();
  const navigate = useNavigate();

  useEffect(() => {
    api.get(`/shop/products/${id}`).then((r) => { setP(r.data); setSize("M"); track("product_view", { product_id: id }); }).catch(() => toast.error("Product not found"));
  }, [id]);

  if (!p) return <div className="min-h-screen bg-app flex items-center justify-center font-mono text-cyan/60 text-sm">LOADING ARTIFACT...</div>;

  const addToCart = () => {
    add(p, size, qty);
    toast.success(`${p.name} (${size}) added`);
  };

  return (
    <div className="min-h-screen bg-app relative pt-10 pb-28" data-testid="product-detail-page">
      <Seo
        title={p.name}
        description={p.description}
        path={`/shop/product/${p.product_id}`}
        image={ogProduct(p.product_id)}
        type="product"
        jsonLd={[
          productJsonLd(p),
          breadcrumbJsonLd([
            { name: "Home", path: "/" },
            { name: "Shop", path: "/shop" },
            { name: p.name, path: `/shop/product/${p.product_id}` },
          ]),
        ]}
      />
      <div className="absolute inset-0 hud-grid opacity-15" />
      <div className="relative mx-auto max-w-6xl px-5">
        <Link to="/shop" className="inline-flex items-center gap-1 font-mono text-[11px] tracking-widest text-slate-400 hover:text-cyan mb-7">
          <ChevronLeft className="w-4 h-4" /> BACK TO DEPOT
        </Link>

        <div className="grid md:grid-cols-2 gap-10">
          <motion.div
            initial={{ opacity: 0, scale: 0.96 }} animate={{ opacity: 1, scale: 1 }}
            className="relative aspect-square border border-cyan/20 brackets overflow-hidden bg-black/40"
          >
            <img src={view === "back" && p.image_back ? p.image_back : p.image} alt={p.name}
              className="w-full h-full object-cover" data-testid="product-detail-image" />
            {p.amazon?.listed && (
              <div className="absolute top-4 left-4 font-mono text-[10px] tracking-widest px-2.5 py-1 bg-app/80 border border-amber/40 text-amber">
                AMAZON CHANNEL
              </div>
            )}
            {p.image_back && (
              <div className="absolute bottom-3 left-1/2 -translate-x-1/2 flex gap-2" data-testid="view-toggle">
                {["front", "back"].map((v) => (
                  <button key={v} onClick={() => setView(v)} data-testid={`view-${v}`}
                    className={`font-mono text-[10px] tracking-widest px-4 py-1.5 border bg-app/70 transition-all ${
                      view === v ? "border-cyan text-cyan box-glow-cyan" : "border-white/15 text-slate-400 hover:border-cyan/50"
                    }`}>
                    {v.toUpperCase()}
                  </button>
                ))}
              </div>
            )}
          </motion.div>

          <div>
            <div className="font-mono text-[10px] tracking-[0.3em] text-slate-500">{p.type?.toUpperCase()}</div>
            <h1 className="font-head text-4xl font-bold uppercase mt-2" style={{ color: p.accent }}>{p.name}</h1>
            <div className="font-head text-3xl font-bold text-slate-100 mt-4">${p.price}</div>
            <p className="text-slate-400 mt-5 leading-relaxed">{p.description}</p>

            <div className="flex items-center gap-2 mt-6 font-mono text-[10px] tracking-widest text-slate-500">
              <MapPin className="w-3.5 h-3.5 text-cyan" /> POD · {p.print_partner?.toUpperCase()} · {p.origin?.toUpperCase()}
            </div>

            {/* size */}
            <div className="mt-7">
              <div className="font-mono text-[10px] tracking-widest text-slate-400 mb-2">SIZE</div>
              <div className="flex flex-wrap gap-2" data-testid="size-selector">
                {p.sizes.map((s) => (
                  <button
                    key={s}
                    onClick={() => setSize(s)}
                    data-testid={`size-${s}`}
                    className={`w-12 h-12 font-head text-sm border transition-all ${
                      size === s ? "border-cyan text-cyan box-glow-cyan" : "border-white/15 text-slate-400 hover:border-cyan/50"
                    }`}
                  >
                    {s}
                  </button>
                ))}
              </div>
            </div>

            {/* qty */}
            <div className="mt-6">
              <div className="font-mono text-[10px] tracking-widest text-slate-400 mb-2">QUANTITY</div>
              <div className="flex items-center border border-white/15 w-fit">
                <button onClick={() => setQty((q) => Math.max(1, q - 1))} data-testid="qty-dec" className="w-10 h-10 text-cyan hover:bg-cyan/10">−</button>
                <span className="w-12 text-center font-mono" data-testid="qty-value">{qty}</span>
                <button onClick={() => setQty((q) => Math.min(10, q + 1))} data-testid="qty-inc" className="w-10 h-10 text-cyan hover:bg-cyan/10">+</button>
              </div>
            </div>

            <div className="flex flex-wrap gap-3 mt-8">
              <button
                onClick={addToCart}
                data-testid="add-to-cart-btn"
                className="btn-hud flex items-center gap-2 border border-cyan text-cyan px-7 py-3 text-sm box-glow-cyan hover:bg-cyan hover:text-app"
              >
                <ShoppingBag className="w-4 h-4" /> Add to Cart
              </button>
              <button
                onClick={() => { add(p, size, qty); navigate("/shop/cart"); }}
                data-testid="buy-now-btn"
                className="btn-hud border border-amber/50 text-amber px-7 py-3 text-sm hover:bg-amber hover:text-app"
              >
                Buy Now
              </button>
            </div>

            {p.amazon_url && (
              <a href={p.amazon_url} target="_blank" rel="noreferrer" className="mt-5 inline-flex items-center gap-1.5 font-mono text-[11px] text-slate-500 hover:text-amber">
                <ExternalLink className="w-3.5 h-3.5" /> ALSO AVAILABLE ON AMAZON
              </a>
            )}
          </div>
        </div>

        <FeaturedOn print={p.print_texture} accent={p.accent} name={p.name} />
      </div>
    </div>
  );
}
